import React, { useEffect, useState } from 'react';

import { Carousel, Embla } from '@mantine/carousel';
import { ActionIcon, Box, Card, Image, Paper, PaperProps, Title, useMantineTheme } from '@mantine/core';
import { IconArrowUpRight, IconChevronLeft, IconChevronRight, IconPhotoOff } from '@tabler/icons-react';
import { BubbleButton } from 'components/SmartAssistantComponents/BubbleButton';
import { useTranslation } from 'react-i18next';
import { ShopifyDisplayedProduct } from 'shared/interfaces/ChatMessage';
import { SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';

const STARTING_SLIDE_INDEX = 1;

interface ShopifyProductsBubbleProps extends PaperProps {
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  products: ShopifyDisplayedProduct[];
}

export function ShopifyProductsBubble({
  products,
  smartAssistantVisualConfig,
  ...paperProps
}: ShopifyProductsBubbleProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const bgColor = smartAssistantVisualConfig.assistantMessageColor;
  const textColor = theme.other.getReadableColor(bgColor);

  const [embla, setEmbla] = useState<Embla | null>(null);
  const [currentSlide, setCurrentSlide] = useState(products.length > 1 ? STARTING_SLIDE_INDEX : 0);
  const lastIndex = products.length - 1;

  useEffect(() => {
    if (embla) embla.scrollTo(STARTING_SLIDE_INDEX, false);
  }, [embla]);

  const handleNext = () => {
    if (embla) {
      embla.scrollNext();
      setCurrentSlide((prev) => Math.min(prev + 1, lastIndex));
    }
  };

  const handlePrev = () => {
    if (embla) {
      embla.scrollPrev();
      setCurrentSlide((prev) => Math.max(prev - 1, 0));
    }
  };

  const ProductCard = ({ ...productData }: ShopifyDisplayedProduct) => (
    <Card
      p={12}
      w={150}
      h={260}
      mx="auto"
      bg={bgColor}
      onClick={() => window.open(productData.url, '_blank')}
      sx={{
        cursor: 'pointer',
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 8
      }}>
      <Image
        radius="sm"
        src={productData.imageUrl}
        withPlaceholder
        style={{ flexGrow: 1 }}
        styles={{
          root: {
            display: 'flex'
          },
          figure: {
            display: 'flex',
            flexGrow: 1
          },
          imageWrapper: {
            display: 'flex',
            flexGrow: 1
          },
          placeholder: {
            backgroundColor: bgColor
          }
        }}
        placeholder={<IconPhotoOff color={textColor} style={{ width: 40, height: 40 }} />}
      />

      <Title order={6} lineClamp={2} lh={1.2}>
        {productData.title}
      </Title>

      <Title order={3}>{productData.price}</Title>

      <BubbleButton
        rightIcon={<IconArrowUpRight />}
        size="xs"
        fullWidth
        smartAssistantVisualConfig={smartAssistantVisualConfig}>
        {t('APP.MORE')}
      </BubbleButton>
    </Card>
  );

  return (
    <Paper withBorder={false} sx={{ position: 'relative' }} bg="transparent" {...paperProps}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          zIndex: 100,
          left: 0,
          right: 0,
          transform: 'translateY(-50%)',
          display: 'flex',
          justifyContent: 'space-between',
          pointerEvents: 'none'
        }}>
        {currentSlide === 0 ? (
          <Box />
        ) : (
          <ActionIcon
            sx={{
              pointerEvents: 'auto',
              marginLeft: -15,
              backgroundColor: bgColor,
              '&:hover': {
                backgroundColor: theme.other.getDimmedColor(bgColor)
              }
            }}
            onClick={handlePrev}>
            <IconChevronLeft color={textColor} />
          </ActionIcon>
        )}

        {currentSlide === lastIndex ? (
          <Box />
        ) : (
          <ActionIcon
            sx={{
              pointerEvents: 'auto',
              marginRight: -15,
              backgroundColor: bgColor,
              '&:hover': {
                backgroundColor: theme.other.getDimmedColor(bgColor)
              }
            }}
            onClick={handleNext}>
            <IconChevronRight color={textColor} />
          </ActionIcon>
        )}
      </Box>
      <Carousel
        align="center"
        px={15}
        slideSize={'33.33%'}
        slideGap="md"
        slidesToScroll={1}
        getEmblaApi={setEmbla}
        breakpoints={[{ maxWidth: 'xs', slideSize: '100%', slideGap: 0 }]}
        withControls={false}>
        {products.map((product, index) => (
          <Carousel.Slide key={index}>
            <ProductCard {...product} />
          </Carousel.Slide>
        ))}
      </Carousel>
    </Paper>
  );
}
